/**
 * Forced MFA gate — first-screen content entrance after the splash handoff
 * (RD-SEC-021 / RD-SEC-021b silk). Same contract as role welcome's
 * playRoleWelcomeEntrance: it starts when the splash starts to LEAVE (t0 of
 * the logo glide), so the wordmark is the main moving thing and the card
 * body arrives under it. Reduced motion / no Web Animations: content is
 * simply there.
 *
 * CALM (KAN-756, 2026-10-10 — "align with the calm sign-in"): the old six
 * separate 480ms fades became TWO quiet beats — the header (bars, title,
 * subtitle) then the body (plan, note, footer) — each settling up 4px on the
 * sign-in's settle curve, starting as the logo is ~60% of the way in. The
 * glide itself is the shared logo-journey leg (600ms), not its own number.
 */
import { prefersReducedMotion } from '../../../ui/splash/splashGlide';
import { LOGO_JOURNEY_LEG_MS } from '../../../ui/splash/logoJourney';
import { AUTH_MOTION } from '../loginAuthTheme';

export const MFA_GATE_GLIDE_MS = LOGO_JOURNEY_LEG_MS;
export const MFA_GATE_CONTENT_AT = Math.round(LOGO_JOURNEY_LEG_MS * 0.6);
const SETTLE_MS = 380;
const SETTLE_PX = 4;

export const MFA_GATE_BEATS = [
  ['bars', 0, ['[data-testid="mfa-header-bars"]', '[data-testid="mfa-stepper-header"]']],
  ['title', 0, ['[data-testid="mfa-gate-intro"] [data-testid="mfa-step-title"]', '[data-testid="mfa-gate-intro"] p:first-of-type']],
  ['sub', 0, ['[data-testid="mfa-gate-intro"] [data-testid="mfa-step-sub"]']],
  ['plan', 90, ['[data-testid="mfa-gate-intro"] ol']],
  ['note', 90, ['[data-testid="mfa-gate-intro"] [data-testid="mfa-no-app"]', '[data-testid="mfa-gate-intro"] > div:last-of-type']],
  ['foot', 90, ['[data-testid="mfa-gate-footer"]', '[data-testid="login-access-card-footer"]']],
];

function findBeat(panel, beat, selectors) {
  const opted = panel.querySelector(`[data-cvx-mfa-beat="${beat}"]`);
  if (opted) return opted;
  for (const sel of selectors) {
    const el = panel.querySelector(sel);
    if (el) return el;
  }
  return null;
}

/**
 * Start the staggered fade on `panel`'s content. Returns a cancel function.
 */
export function playMfaGateEntrance(panel, { startAt = MFA_GATE_CONTENT_AT } = {}) {
  if (!panel || typeof panel.querySelector !== 'function' || prefersReducedMotion()) return () => {};
  const anims = [];
  MFA_GATE_BEATS.forEach(([beat, offset, selectors]) => {
    const el = findBeat(panel, beat, selectors);
    if (!el || typeof el.animate !== 'function') return;
    const anim = el.animate([
      { opacity: 0, transform: `translateY(${SETTLE_PX}px)` },
      { opacity: 1, transform: 'none' },
    ], {
      duration: SETTLE_MS, delay: startAt + offset, easing: AUTH_MOTION.settleEase, fill: 'both',
    });
    anim.finished?.then(() => anim.cancel()).catch(() => {});
    anims.push(anim);
  });
  return () => anims.forEach((a) => { try { a.cancel(); } catch (_e) { /* gone */ } });
}
